/* ─────────────────────────────────────────────
   SIDEBAR — session/ungrouped/by-device lists (Cluster 8)
   renderSessions, renderSessionsList, renderUngroupedList, renderByDeviceList,
   renderDeviceHeader, cohortScopeBadge, emptyPlaceholder
   ───────────────────────────────────────────── */
import { $, qsa, escapeHtml } from './dom';
import { fmtDateRange, fmtRelative, sparkPath } from './format';
import { byKey, isHighEntropyDevice, segAvatarsHtml, deviceDisplayName } from './animals';
import { state } from '../state/app-state';
import { MOCK } from '../state/device';
import { groupSessionsLocal, colorForToken } from '../state/cohort-model';
import { isDeviceLive, lensWatchUrl } from '../api/live-badge';
import { loadDeviceHistory } from '../storage/persistence';
import { selectSession, focusPing, drawRoutes } from '../map/tracker';

// Callbacks injected by main.ts to avoid circular imports.
// setSyncReplayCb / setEditCurrentDeviceCb / setRenderDrawerActiveCb /
// setGetLastRecPingCb are called at main.ts module scope after the
// target functions are defined.
let _syncReplayCb: (() => void) | null = null;
export function setSyncReplayCb(fn: () => void): void { _syncReplayCb = fn; }
let _editCurrentDeviceCb: (() => void) | null = null;
export function setEditCurrentDeviceCb(fn: () => void): void { _editCurrentDeviceCb = fn; }
let _renderDrawerActiveCb: (() => void) | null = null;
export function setRenderDrawerActiveCb(fn: () => void): void { _renderDrawerActiveCb = fn; }
let _getLastRecPingCb: (() => number | null) | null = null;
export function setGetLastRecPingCb(fn: () => number | null): void { _getLastRecPingCb = fn; }

export function renderSessions(){
  const list = $('session-list');
  const sub  = $('sb-sub');
  const title= $('sb-title');
  $('ping-count').textContent = `${MOCK.pings.length.toLocaleString()} pings`;

  // Sync tabs
  qsa('.sb-tab').forEach(t => t.classList.toggle('active', t.dataset.view === state.viewMode));

  if(state.viewMode === 'sessions'){
    // F4: only the sessions view consumes the grouped result, so compute it
    // here instead of on every renderSessions() call (ungrouped/devices don't
    // use `sessions` — they read state.cohort / MOCK.pings).
    const sessions = groupSessionsLocal(MOCK.pings);
    title.textContent = 'Pings';
    sub.textContent = 'grouped by time gaps · tap to focus';
    $('session-count').textContent = `${MOCK.pings.length} pings · ${sessions.length} sessions`;
    renderSessionsList(list, sessions);
  } else if(state.viewMode === 'ungrouped'){
    title.textContent = 'Pings';
    sub.textContent = 'individual points · newest first';
    $('session-count').textContent = `${MOCK.pings.length} pings`;
    renderUngroupedList(list);
  } else {
    title.textContent = 'Devices';
    if(MOCK.device && MOCK.device.group){
      const groupName = MOCK.device.group.map(k=>byKey[k]?.name || k).join(' · ');
      const count = state.cohort.size;
      sub.innerHTML = `group <b>${groupName}</b> · ${count} ${count === 1 ? 'device' : 'devices'}`;
      $('session-count').textContent = `${count} ${count === 1 ? 'device' : 'devices'}`;
    } else {
      sub.textContent = 'no group set on this device';
      $('session-count').textContent = `— devices`;
    }
    renderByDeviceList(list);
  }
  // Owner-surface chokepoint: every owner data/view change funnels through
  // here, so refresh the replay scrubber's range + visibility from one place
  // (devices mode hides it). syncReplay never re-renders the map, so this
  // can't recurse with drawRoutes.
  _syncReplayCb?.();
}

export function renderSessionsList(list: any, sessions: any[]){
  if(sessions.length === 0){
    list.innerHTML = emptyPlaceholder();
    return;
  }
  list.innerHTML = sessions.map((s: any) => `
    <div class="session-row ${s.id===state.selectedSession?'active':''}" data-id="${s.id}">
      <svg class="spark" viewBox="0 0 56 30" preserveAspectRatio="none"><path d="${sparkPath(s.coords)}"/></svg>
      <div class="sess-meta">
        <div class="name">${fmtDateRange(s.startT, s.endT)}</div>
        <div class="stats">${s.points.length} pings · every ${s.cadence_s}s</div>
      </div>
      <div class="sess-time">${fmtRelative(s.endT)}</div>
    </div>
  `).join('');
  qsa('.session-row', list).forEach(r => r.onclick = () => selectSession(r.dataset.id));
}

export function emptyPlaceholder(){
  return `
    <div class="session-row" style="opacity:.6; cursor:default">
      <div class="sess-meta">
        <div class="name">No pings yet</div>
        <div class="stats">press <em>start recording</em> below</div>
      </div>
    </div>
  `;
}

export function renderUngroupedList(list: any){
  if(MOCK.pings.length === 0){
    list.innerHTML = emptyPlaceholder();
    return;
  }
  // newest first, cap to 300 for sanity
  const rows = MOCK.pings.slice().reverse().slice(0, 300);
  const lastRecPing = _getLastRecPingCb?.() ?? null;
  list.innerHTML = rows.map((p, i) => {
    const d = new Date(p.t);
    const time = d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',second:'2-digit'});
    const day  = d.toLocaleDateString('en-GB',{day:'2-digit',month:'short'});
    const isRec = lastRecPing && p.t === lastRecPing && i === 0;
    return `<div class="ping-row ${isRec?'is-rec':''}" data-t="${p.t}" data-lat="${p.lat}" data-lng="${p.lng}">
      <div class="pt">${time}<span class="pt-d">${day}</span></div>
      <div class="pc">${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}</div>
      <div class="pr">${fmtRelative(p.t)}</div>
    </div>`;
  }).join('');
  qsa('.ping-row', list).forEach(r => r.onclick = () => {
    qsa('.ping-row', list).forEach(x => x.classList.remove('active'));
    r.classList.add('active');
    focusPing(+r.dataset.lat, +r.dataset.lng);
  });
}

// Honest cohort-scope badge: when a device limits how it appears in the cohort
// (coarsened precision and/or a clamped range), show it so peers see WHY a
// device's trail is fuzzed/short, not silently degraded. Default scope
// ({all, exact}) renders nothing.
export function cohortScopeBadge(scope: { range: string; precision: string } | undefined): string {
  if(!scope) return '';
  const parts: string[] = [];
  if(scope.precision === '1km') parts.push('~1 km');
  else if(scope.precision === 'city') parts.push('~10 km');
  if(scope.range === '24h') parts.push('24h');
  else if(scope.range === '7d') parts.push('7d');
  if(parts.length === 0) return '';
  return ` · <span class="dr-scope" title="This device limits how it appears in the cohort (its own map stays exact)">${parts.join(' · ')}</span>`;
}

export function renderByDeviceList(list: any){
  if(!MOCK.device || !MOCK.device.group){
    list.innerHTML = `<div class="empty-state">
      <h4>No <em>group</em> set</h4>
      Devices sharing a <em>group prefix</em> appear here together &mdash; useful for a fleet of beacons,
      a family's keys, or a team's tracker tags.
      <p class="es-note">A group is a shared channel. With no group set, this device isn't in any cohort &mdash; nothing here is shared or discoverable.</p>
      <button class="es-cta" id="empty-set-group">Edit identifier</button>
    </div>`;
    const btn = $('empty-set-group');
    if(btn) btn.onclick = () => _editCurrentDeviceCb?.();
    return;
  }
  const localToken = MOCK.device.token;
  // Stable display order: current device pinned at the top, then everyone
  // else alphabetical by display name. Deliberately NOT sorted by latest
  // ping — a recency sort reshuffles the whole list every time any device
  // pings, which is unusable with an active cohort. The token tiebreaker
  // keeps two same-named devices from swapping places. Recency is still
  // visible per row via the "Xs ago" timestamp.
  const nameFor = (d: any) => d.animals.map((k: string) => byKey[k]?.name || k).join(' · ');
  const all = Array.from(state.cohort.values()).sort((a, b) => {
    if(a.token === localToken) return -1;
    if(b.token === localToken) return 1;
    return nameFor(a).localeCompare(nameFor(b)) || a.token.localeCompare(b.token);
  });
  const others = all.filter(d => d.token !== localToken).length;
  const hiddenCount = all.filter(d => d.token !== localToken && state.hiddenCohort.has(d.token)).length;
  const hint = (others === 0)
    ? `<div class="empty-state" style="margin-top:.5em">
         no other devices yet &mdash; share the group prefix to invite a friend
       </div>` : '';
  // Honest-disclosure copy — a group is a semi-public, shared channel; this
  // view must never imply privacy (see 2026-05-22-locus-cohort-coop-view.md).
  const disclosure = `<div class="dr-disclosure"><i class="ph ph-users-three" aria-hidden="true"></i><span>A group is a <b>shared channel</b> — anyone who knows this group can see everyone's location here.</span></div>`;
  // Show/hide-all toolbar — only meaningful once there's a peer to toggle.
  const toolbar = (others === 0) ? '' : `
    <div class="dr-toolbar">
      <span class="dr-toolbar-status">${hiddenCount ? `${hiddenCount} hidden` : 'all shown'}</span>
      <span class="dr-toolbar-actions">
        <button class="dr-all" id="dr-show-all"${hiddenCount === 0 ? ' disabled' : ''}>show all</button>
        <button class="dr-all" id="dr-hide-all"${hiddenCount === others ? ' disabled' : ''}>hide all</button>
      </span>
    </div>`;
  const history = loadDeviceHistory();   // F3: parse once for the whole list
  list.innerHTML = disclosure + toolbar + all.map(d => {
    const isCur = d.token === localToken;
    // Viewer-local hide state; the current device is the anchor — never hidden.
    const hidden = !isCur && state.hiddenCohort.has(d.token);
    const pings = isCur ? MOCK.pings : d.pings;
    const last  = pings.length ? pings[pings.length-1].t : (d.lastT || 0);
    // Server-driven session_id boundaries for both local + cohort peers
    // (cohort endpoint echoes session_id as of V1.3.1). groupSessionsLocal
    // falls back to gap-clustering for pings whose session_id is null.
    const sessCount = groupSessionsLocal(pings).length;
    const swatch = isCur ? '#B84A22' : colorForToken(d.token);
    // Eye toggle: enabled for peers, locked-visible for the current device
    // (shown for list consistency but disabled — it stays pinned).
    const eye = isCur
      ? `<button class="dr-eye is-locked" disabled aria-disabled="true" title="you are here — always shown" aria-label="Your device, always shown"><i class="ph ph-eye"></i></button>`
      : `<button class="dr-eye${hidden ? ' is-off' : ''}" data-token="${d.token}" title="${hidden ? 'show on map' : 'hide from map'}" aria-label="${hidden ? 'Show this device on the map' : 'Hide this device from the map'}" aria-pressed="${hidden ? 'true' : 'false'}"><i class="ph ${hidden ? 'ph-eye-slash' : 'ph-eye'}"></i></button>`;
    // Optional lens live badge (Phase 4): a small "🔴 live · watch" affordance
    // when lens reports this device's token as streaming. Fail-open — when the
    // flag is OFF or lens is unreachable, isDeviceLive() is always false and the
    // row renders exactly as before.
    const liveBadge = isDeviceLive(d.token)
      ? `<a class="dr-live" data-watch-token="${escapeHtml(d.token)}" href="${lensWatchUrl(d.token)}" target="_blank" rel="noopener" title="This device is streaming live video — open the lens viewer"><span class="dr-live-dot" aria-hidden="true"></span>live<span class="dr-live-watch">watch</span></a>`
      : '';
    return `<div class="device-row ${isCur?'is-active':''}${hidden ? ' is-hidden' : ''}" data-token="${d.token}" data-last-lat="${pings.length ? pings[pings.length-1].lat : ''}" data-last-lng="${pings.length ? pings[pings.length-1].lng : ''}">
      <div class="device-row-trio" style="border-left:3px solid ${swatch}; padding-left:.4em">${segAvatarsHtml(d.animals, swatch)}</div>
      <div class="dr-body">
        <div class="dr-name">${deviceDisplayName(d.token, d.animals, null, history, d.tag)}${isCur?'<span class="dr-cur">current</span>':''}${liveBadge}</div>
        <div class="dr-meta">${pings.length} pings · ${sessCount} sessions${d.truncated ? ' · truncated' : ''}${cohortScopeBadge(d.cohort_scope)}</div>
      </div>
      <div class="dr-time">${last ? fmtRelative(last) : '—'}<br/>${d.token.split('-').slice(-1)[0]}</div>
      ${eye}
    </div>`;
  }).join('') + hint;
  qsa('.device-row', list).forEach(r => r.onclick = () => {
    const lat = parseFloat(r.dataset.lastLat);
    const lng = parseFloat(r.dataset.lastLng);
    if(Number.isFinite(lat) && Number.isFinite(lng)) focusPing(lat, lng);
  });
  // The "watch" link opens the lens viewer in a new tab; stop it from also
  // focusing/selecting the row underneath (the <a>'s href does the navigation).
  qsa('.dr-live', list).forEach((a: any) => a.addEventListener('click', (e: any) => e.stopPropagation()));
  // Per-peer hide toggle. stopPropagation so it doesn't also focus the row.
  // Re-render the list (refresh eye icon + toolbar) and redraw the map.
  qsa('.dr-eye', list).forEach((b: any) => b.addEventListener('click', (e: any) => {
    e.stopPropagation();
    const tok = b.dataset.token;
    if(!tok || tok === localToken) return;
    if(state.hiddenCohort.has(tok)) state.hiddenCohort.delete(tok);
    else state.hiddenCohort.add(tok);
    drawRoutes();
    renderByDeviceList(list);
  }));
  const showAllBtn = $('dr-show-all');
  if(showAllBtn) showAllBtn.onclick = () => {
    if(state.hiddenCohort.size === 0) return;
    state.hiddenCohort.clear();
    drawRoutes();
    renderByDeviceList(list);
  };
  const hideAllBtn = $('dr-hide-all');
  if(hideAllBtn) hideAllBtn.onclick = () => {
    for(const d of all) if(d.token !== localToken) state.hiddenCohort.add(d.token);
    drawRoutes();
    renderByDeviceList(list);
  };
}

export function renderDeviceHeader(){
  if(!MOCK.device) return;
  const d = MOCK.device;
  const color = colorForToken(d.token);
  const gtrio = $('group-trio');
  const idSlash = $('id-slash');
  if(d.group){
    gtrio.innerHTML = segAvatarsHtml(d.group, color);
    gtrio.style.display = '';
    idSlash.style.display = '';
  } else {
    gtrio.innerHTML = '';
    gtrio.style.display = 'none';
    idSlash.style.display = 'none';
  }
  const trio = $('device-trio');
  trio.innerHTML = segAvatarsHtml(d.animals, color);
  // A server display tag (ADR 0022), when set, is the name for ANY device
  // (animal or high-entropy). deviceDisplayName escapes it, so innerHTML here is
  // safe escaped-interpolation (matches the animal branch below).
  if(d.tag || isHighEntropyDevice(d.animals)){
    $('device-name-display').innerHTML = deviceDisplayName(d.token, d.animals, d.group, undefined, d.tag);
  } else {
    const names = d.animals.map((k: string) => byKey[k]?.name || k);
    const gHtml = d.group
      ? `<span class="name-group">${d.group.map((k: string)=>byKey[k]?.name||k).join(' · ')}</span><span class="name-slash">/</span>`
      : '';
    $('device-name-display').innerHTML = gHtml + names.map((n: string,i: number)=> i===1?`<em>${n}</em>`:n).join(' · ');
  }
  $('device-token-display').textContent = d.token;
  // The active-device card in the drawer mirrors this header; keep it fresh.
  _renderDrawerActiveCb?.();
}
